import { UnitValue } from '../data-structures/unit-value.model';
import { InputSummary, InputSummaryItem } from '../input-summary';
import InputSummaryUtils from '../../helper/input-summary/input-summary-util';
import Utils from '../../util';
import { v4 as uuid } from 'uuid';
import { AnessaListItem } from '../anessa-list-item.interface';
import { LocaleState, LocaleCountry, LocaleCity } from '../interfaces/locale.interface';


export interface ProjectInfoInterface {
  title: string;
  startDate: Date;
  endDate: Date;
  locale: ProjectLocale,
  location: string;
  stage: string;
  description: string;
  objectives: string;
  members: ProjectMember[];
  notes: string;
  attachedFiles: ProjectFile[];
  checklists: ProjectChecklist[];
  showChecklistSectionsForm: boolean;
  scopingTests: ProjectChecklist;
  defaults: ProjectDefaults;
  exchangeRate: string;
  singleCurrencyUsed: boolean;
  baseCurrencyId: string;
  useFinancial: boolean;
  useGHG: boolean;
  isDraftSubmission: boolean;
  isSubmittedProject: boolean;
  isApprovedProject: boolean;
  isArchivedProject: boolean;
  isNewProject: boolean;
}

export class ProjectInfo {
  public title: string;
  public startDate: Date;
  public endDate: Date;
  public locale: ProjectLocale;
  public location: string;
  public stage: string;
  public description: string;
  public objectives: string;
  public members: ProjectMember[];
  notes: string;
  attachedFiles: ProjectFile[];
  checklists: ProjectChecklist[];
  showChecklistSectionsForm: boolean;
  scopingTests: ProjectChecklist;
  defaults: ProjectDefaults;
  exchangeRate: string;
  singleCurrencyUsed: boolean;
  baseCurrencyId: string;
  useFinancial: boolean;
  useGHG: boolean;
  isDraftSubmission: boolean;
  isSubmittedProject: boolean;
  isApprovedProject: boolean;
  isArchivedProject: boolean;
  isNewProject: boolean;

  constructor(param: ProjectInfoInterface = {
    title: '',
    startDate: null,
    endDate: null,
    locale: new ProjectLocale(),
    location: '',
    stage: '',
    description: '',
    objectives: '',
    members: [],
    notes: '',
    attachedFiles: [],
    checklists: [],
    showChecklistSectionsForm: false,
    scopingTests: null,
    defaults: new ProjectDefaults(),
    exchangeRate: null,
    singleCurrencyUsed: false,
    baseCurrencyId: '000000000000000000002004',  // CAD
    useFinancial: true,
    useGHG: true,
    isDraftSubmission: false,
    isSubmittedProject: false,
    isApprovedProject: true,
    isArchivedProject: false,
    isNewProject: false,
  }) {
    this.title = param.title;
    this.startDate = param.startDate;
    this.endDate = param.endDate;
    this.locale = new ProjectLocale(param.locale);
    this.location = param.location;
    this.stage = param.stage;
    this.description = param.description;
    this.objectives = param.objectives;
    this.members = [];
    for (const member of param.members) {
      this.members.push(new ProjectMember(member));
    }
    this.notes = param.notes;
    this.attachedFiles = [];
    if (param.attachedFiles) {
      for (const file of param.attachedFiles) {
        this.attachedFiles.push(file);
      }
    }
    this.checklists = [];
    if (param.checklists) {
      for (const checklist of param.checklists) {
        if (checklist.displayMethod !== 'form' && checklist.displayMethod !== 'tabs' && param.showChecklistSectionsForm === true) {
          checklist.displayMethod = 'form';
        }
        this.checklists.push(new ProjectChecklist(checklist));
      }
    }
    this.showChecklistSectionsForm = param.showChecklistSectionsForm ?? false;

    if (param.scopingTests) {
      this.scopingTests = new ProjectChecklist(param.scopingTests);
    }
    this.defaults = new ProjectDefaults(param.defaults);
    this.exchangeRate = param.exchangeRate;
    this.singleCurrencyUsed = param.singleCurrencyUsed;
    this.baseCurrencyId = param.baseCurrencyId;
    if (Utils.isBoolean(param.useFinancial)) {
      this.useFinancial = param.useFinancial;
    } else {
      this.useFinancial = true;
    }
    if (Utils.isBoolean(param.useGHG)) {
      this.useGHG = param.useGHG;
    } else {
      this.useGHG = true;
    }
    this.isDraftSubmission = param.isDraftSubmission;
    this.isSubmittedProject = param.isSubmittedProject;
    this.isApprovedProject = param.isApprovedProject;
    this.isArchivedProject = param.isArchivedProject;
    this.isNewProject = param.isNewProject;
  }

  static deepCopy(projInfo: ProjectInfo): ProjectInfo {
    let copy;
    if (projInfo) {
      copy = new ProjectInfo(projInfo);
      return copy;
    }
    throw new Error('Project info copy failed');
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    const defaultUnitsInputSummary = new InputSummary();
    defaultUnitsInputSummary.subSection[''] = this.getDefaultUnitsSubsection(path);
    inputSummary.subSection['PROJECT.PROJECT_INFO.DEFAULT_UNITS'] = defaultUnitsInputSummary;
    return inputSummary;
  }

  getDefaultUnitsSubsection(path: string[]) {
    const inputSummary = new InputSummary();

    let inputItems: InputSummaryItem[] = [];
    // tslint:disable-next-line: forin
    for (const key in this.defaults) {
      inputItems = InputSummaryUtils.performPush(inputItems, ProjInfoProps[key], this.defaults[key], [...path, 'defaults', key]);
    }

    inputSummary.inputs = inputItems;
    return inputSummary;
  }
}

export enum ProjInfoProps {
  title = 'INPUT_SUMMARY.PROJECT_INFO.TITLE',
  startDate = 'INPUT_SUMMARY.PROJECT_INFO.START_DATE',
  endDate = 'INPUT_SUMMARY.PROJECT_INFO.END_DATE',
  liquid = 'INPUT_SUMMARY.PROJECT_INFO.LIQUID',
  solid = 'INPUT_SUMMARY.PROJECT_INFO.SOLID',
  gas = 'INPUT_SUMMARY.PROJECT_INFO.GAS',
  currency = 'INPUT_SUMMARY.PROJECT_INFO.CURRENCY',
  period = 'INPUT_SUMMARY.PROJECT_INFO.PERIOD',
  cpi = 'INPUT_SUMMARY.PROJECT_INFO.CPI',
  decimalPrecision = 'INPUT_SUMMARY.PROJECT_INFO.DECIMAL_PRECISION',

}

export interface ProjectMemberInterface {
  name: string;
  uuid: string;
  email: string;
  title: string;
  accessToken: string;
}

export class ProjectMember implements AnessaListItem {
  name: string;
  uuid: string;
  email: string;
  title: string;
  accessToken: string;

  constructor(param: ProjectMemberInterface = {
    name: '',
    uuid: null,
    email: '',
    title: '',
    accessToken: '',
  }) {
    this.name = param.name;
    this.uuid = param.uuid ? param.uuid : uuid();
    this.email = param.email;
    this.title = param.title;
    this.accessToken = param.accessToken;
  }

  static deepCopy(member: ProjectMember): ProjectMember {
    let copy;
    if (member) {
      copy = new ProjectMember(member);
      return copy;
    }
    throw new Error('Project member copy failed');
  }
}

export interface ProjectFileInterface {
  name: string;
  uuid: string;
  fileId: string;
  fileExt: string;
  fileSize: number;
}

export class ProjectFile implements AnessaListItem {
  name: string;
  uuid: string;
  fileId: string;
  fileExt: string;
  fileSize: number;

  constructor(param: ProjectFileInterface = {
    name: null,
    uuid: null,
    fileId: null,
    fileExt: null,
    fileSize: 0,
  }) {
    this.name = param.name;
    this.uuid = param.uuid ? param.uuid : uuid();
    this.fileId = param.fileId;
    this.fileExt = param.fileExt;
    this.fileSize = param.fileSize;
  }

  static deepCopy(file: ProjectFile): ProjectFile {
    let copy;
    if (file) {
      copy = new ProjectFile(file);
      return copy;
    }
    throw new Error('Project file copy failed');
  }
}

export interface ProjectChecklistColumnInterface {
  uuid: string;
  name: string;
  acceptedItems?: string[];
  cellDropdownItems?: any;
  locked?: boolean;
  align?: string;
  isIcon?: boolean;
}

export class ProjectChecklistColumn {
  uuid: string;
  name: string;
  acceptedItems: string[];
  cellDropdownItems: any;
  locked: boolean;
  align: string;
  isIcon: boolean;

  constructor(param: ProjectChecklistColumnInterface = {
    uuid: null,
    name: null,
    acceptedItems: [],
    cellDropdownItems: {},
    locked: false,
    align: '',
    isIcon: false
  }) {
    this.uuid = param.uuid ? param.uuid : uuid();
    this.name = param.name;
    this.locked = param.locked;
    this.acceptedItems = [];
    if (param.acceptedItems) {
      for (const item of param.acceptedItems) {
        this.acceptedItems.push(item);
      }
    }
    this.cellDropdownItems = {};
    if (param.cellDropdownItems) {
      const keys = Object.keys(param.cellDropdownItems);
      if (keys && keys.length > 0) {
        for (const key of keys) {
          this.cellDropdownItems[key] = new ChecklistCellDataItem(param.cellDropdownItems[key]);
        }
      }
    }
    this.align = param.align? param.align : 'center';
    this.isIcon = param.isIcon;
  }

  static deepCopy(column: ProjectChecklistColumn): ProjectChecklistColumn {
    if (column) {
      return new ProjectChecklistColumn(column);
    }
    throw new Error('Project checklist column copy failed');
  }
}

export interface CellDropdownItemInterface {
  items: string[];
}

export class ChecklistCellDataItem {
  items: string[];

  constructor(param: CellDropdownItemInterface = {
    items: []
  }) {
    this.items = [];
    if (param.items && param.items.length > 0) {
      this.items = [...param.items];
    }
  }

  static deepCopy(dataItem: ChecklistCellDataItem): ChecklistCellDataItem {
    if (dataItem) {
      return new ChecklistCellDataItem(dataItem);
    }
    return null;
  }
}

export interface ProjectChecklistRowInterface {
  level: number;
  children: ProjectChecklistRowInterface[];
  data: any;
  dataPlaceholder: any;
  starred: boolean;
  rangeMin?: number;
  rangeMax?: number;
  flag?: string;
  anessaCheck?: boolean;
  locked?: boolean;
  includeHeader?: boolean;
  sectionType?: string;
  columnSettings?: ProjectChecklistColumnInterface[];
  uuid: string;
}

export class ProjectChecklistRow {
  level: number;
  children: ProjectChecklistRow[];
  starred: boolean;
  data: any;
  dataPlaceholder: any;
  rangeMin: number;
  rangeMax: number;
  flag: string;
  anessaCheck: boolean;
  locked: boolean;
  includeHeader: boolean;
  sectionType: string;
  columnSettings: ProjectChecklistColumn[];
  uuid: string;

  constructor(param: ProjectChecklistRowInterface = {
    level: 1,
    children: [],
    data: {},
    dataPlaceholder: {},
    starred: false,
    rangeMin: null,
    rangeMax: null,
    flag: null,
    anessaCheck: false,
    locked: false,
    includeHeader: true,
    sectionType: '',
    columnSettings: [],
    uuid: null
  }) {
    this.level = param.level;
    this.children = [];
    if (param.children) {
      for (const child of param.children) {
        this.children.push(new ProjectChecklistRow(child));
      }
    }
    this.data = JSON.parse(JSON.stringify(param.data));
    if (param.dataPlaceholder) this.dataPlaceholder = JSON.parse(JSON.stringify(param.dataPlaceholder));
    else this.dataPlaceholder = {};

    if (param.starred) {
      this.starred = param.starred;
    }
    this.rangeMin = param.rangeMin;
    this.rangeMax = param.rangeMax;
    this.flag = param.flag;
    this.anessaCheck = param.anessaCheck;
    this.locked = param.locked;
    this.includeHeader = param.includeHeader ?? true;
    this.sectionType = param.sectionType;
    this.columnSettings = [];
    if (param.columnSettings) {
      for (const col of param.columnSettings) {
        this.columnSettings.push(new ProjectChecklistColumn(col));
      }
    }
    this.uuid = param.uuid ? param.uuid : uuid();
  }

  static deepCopy(row: ProjectChecklistRow): ProjectChecklistRow {
    if (row) {
      return new ProjectChecklistRow(row);
    }
    throw new Error('Project checklist row copy failed');
  }
}

export interface ProjectChecklistInterface {
  name: string;
  created: Date;
  lastModified: Date;
  displayMethod: string;
  columns: ProjectChecklistColumn[];
  rows: ProjectChecklistRow[];
}

export class ProjectChecklist {
  name: string;
  created: Date;
  lastModified: Date;
  displayMethod: string;
  columns: ProjectChecklistColumn[];
  rows: ProjectChecklistRow[];

  constructor(param: ProjectChecklistInterface = {
    name: null,
    created: null,
    lastModified: null,
    displayMethod: '',
    columns: [],
    rows: [],
  }) {
    this.name = param.name;
    this.created = param.created;
    this.lastModified = param.lastModified;
    this.displayMethod = param.displayMethod;
    this.columns = [];
    for (const column of param.columns) {
      this.columns.push(new ProjectChecklistColumn(column));
    }
    this.rows = [];
    for (const row of param.rows) {
      this.rows.push(new ProjectChecklistRow(row));
    }
  }

  static deepCopy(checklist: ProjectChecklist): ProjectChecklist {
    if (checklist) {
      return new ProjectChecklist(checklist);
    }
    throw new Error('Project checklist copy failed');
  }

  static arrayDeepCopy(checklists: ProjectChecklist[]): ProjectChecklist[] {
    const copy: ProjectChecklist[] = [];
    if (checklists && checklists.length > 0) {
      for (const checklist of checklists) {
        copy.push(new ProjectChecklist(checklist));
      }
    }
    return copy;
  }

  getColumnByName(name: string) {
    for (const column of this.columns) {
      if (column.name === name) {
        return column;
      }
    }
    return null;
  }
}

export interface DefaultUnitsInterface {
  liquid: string;
  solid: string;
  gas: string;
  currency: string;
  period: string;
  cpi: UnitValue;
  decimalPrecision: UnitValue;
  unitSystem: string;
  temperature:string;
}

export class ProjectDefaults {
  public liquid: string;
  public solid: string;
  public gas: string;
  public currency: string;
  public period: string;
  public cpi: UnitValue;
  public decimalPrecision: UnitValue;
  public unitSystem: string;
  public temperature:string;

  constructor(param: DefaultUnitsInterface = {
    liquid: null,
    solid: null,
    gas: null,
    currency: null,
    period: null,
    temperature: null,
    cpi: new UnitValue(),
    decimalPrecision: new UnitValue(),
    unitSystem: null,
  }) {
    this.liquid = (param.liquid === '') ? null : param.liquid;
    this.solid = (param.solid === '') ? null : param.solid;
    this.gas = (param.gas === '') ? null : param.gas;
    this.currency = (param.currency === '') ? null : param.currency;
    this.period = (param.period === '') ? null : param.period;
    this.cpi = new UnitValue(param.cpi);
    this.decimalPrecision = new UnitValue(param.decimalPrecision);
    this.unitSystem = (param.unitSystem === '') ? null : param.unitSystem;
    this.temperature = (param.temperature === '') ? null : param.temperature;
  }

  static deepCopy(defaultUnits: ProjectDefaults): ProjectDefaults {
    let copy;
    if (defaultUnits) {
      copy = new ProjectDefaults(defaultUnits);
      return copy;
    }
    throw new Error('default units copy failed');
  }
}

export interface ProjectLocaleInterface {
  country: LocaleCountry;
  state: LocaleState;
  city: LocaleCity;
}

export class ProjectLocale {
  public country: LocaleCountry;
  public state: LocaleState;
  public city: LocaleCity;

  constructor(param: ProjectLocaleInterface = {
    country: null,
    state: null,
    city: null
  }) {
    this.country = param.country;
    this.state = param.state;
    this.city = param.city;
  }

  static deepCopy(locale: ProjectLocale): ProjectLocale {
    if (locale) {
      return new ProjectLocale(locale);
    }
    throw new Error('Project info locale copy failed');
  }
}
